import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, FormControl, Select, MenuItem, Button } from '@mui/material';
import axios from 'axios';
import { fieldSx, labelSx, multilineFieldSx, menuProps } from '../../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

const COSEGURO_COLORES = {
  cobrado: { border: '#4caf5066', headerBg: '#0d1e12', headerColor: '#4caf50', labelColor: '#81c784', reciboBg: 'rgba(76,175,80,0.06)' },
  exento:  { border: '#78909c55', headerBg: '#151d22', headerColor: '#78909c', labelColor: '#90a4ae', reciboBg: 'rgba(176,190,197,0.05)' },
};

const TIPO_LABEL = {
  COBRADO:   { label: 'Cobrado',   color: '#81c784' },
  EXENTO:    { label: 'Exento',    color: '#90a4ae' },
  CORREGIDO: { label: 'Corregido', color: '#ef5350' },
  ANULADO:   { label: 'Anulado',   color: '#546e7a' },
};

function fmtFechaHora(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function LF({ label, children }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function TabCoseguro({ form, setForm, ejecutarCoseguro, corregirCoseguro, anularCoseguro, puedeEditarCoseguro, esAdmin, turnoId }) {
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [motivoCorrecion, setMotivoCorrecion] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [conceptoTipo, setConceptoTipo] = useState('coseguro');
  const [conceptoTexto, setConceptoTexto] = useState('');
  const [historial, setHistorial] = useState([]);

  useEffect(() => {
    if (!turnoId) return;
    axios.get(`${API}/api/coseguros?turno_id=${turnoId}`)
      .then(r => setHistorial(r.data.data || []))
      .catch(() => {});
  }, [turnoId, form.coseguro_estado]);

  const cs = form.coseguro_estado;
  const col = cs ? COSEGURO_COLORES[cs] : null;
  const LABEL = {
    cobrado: '✓ Cobrado',
    exento:  '✓ Exento',
  };
  const conceptoValido = conceptoTipo === 'coseguro' || conceptoTexto.trim().length > 0;
  const conceptoFinal  = conceptoTipo === 'coseguro' ? 'Coseguro' : conceptoTexto.trim();

  async function handleCorregir() {
    if (!motivoCorrecion.trim()) return;
    await corregirCoseguro(motivoCorrecion.trim());
    setCorrigiendo(false);
    setMotivoCorrecion('');
  }

  async function handleAnular() {
    if (!motivoAnulacion.trim()) return;
    await anularCoseguro(motivoAnulacion.trim());
    setAnulando(false);
    setMotivoAnulacion('');
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

      {/* Card principal */}
      <Box sx={{ borderRadius: 2, border: `1.5px solid ${col ? col.border : '#37474f'}`, overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ px: 2, py: 1.2, bgcolor: col ? col.headerBg : '#161f30', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: col ? col.headerColor : '#607d8b', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            Coseguro
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: col ? col.labelColor : '#ef9a9a' }}>
            {cs ? LABEL[cs] : '⚠ Sin resolver'}
          </Typography>
        </Box>

        {!cs ? (
          <>
            {/* Concepto */}
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography sx={labelSx}>Concepto</Typography>
              <Box sx={{ display: 'flex', gap: '6px', mt: '6px' }}>
                {[{ key: 'coseguro', label: 'Coseguro' }, { key: 'otro', label: 'Otro' }].map(opt => (
                  <Box key={opt.key}
                    onClick={() => { setConceptoTipo(opt.key); if (opt.key === 'coseguro') setConceptoTexto(''); }}
                    sx={{
                      px: '12px', py: '5px', borderRadius: '6px', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, border: '1px solid', transition: 'all 0.12s',
                      borderColor: conceptoTipo === opt.key ? 'rgba(144,202,249,0.4)' : 'rgba(144,202,249,0.12)',
                      bgcolor:     conceptoTipo === opt.key ? 'rgba(144,202,249,0.1)'  : 'transparent',
                      color:       conceptoTipo === opt.key ? '#90caf9'                : '#546e7a',
                    }}>
                    {opt.label}
                  </Box>
                ))}
              </Box>
              {conceptoTipo === 'otro' && (
                <TextField fullWidth autoFocus
                  value={conceptoTexto}
                  onChange={e => setConceptoTexto(e.target.value)}
                  placeholder="Ej: Certificado médico, Consulta particular..."
                  disabled={!puedeEditarCoseguro}
                  sx={{ ...fieldSx, mt: '8px' }} />
              )}
            </Box>
            <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <LF label="Monto">
                <TextField type="number" fullWidth
                  value={form.coseguro_monto}
                  onChange={e => {
                    const val = e.target.value.replace(/[.,]/g, '');
                    if (/^\d*$/.test(val)) setForm(f => ({ ...f, coseguro_monto: val }));
                  }}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, step: 1 }}
                  disabled={!puedeEditarCoseguro}
                  sx={fieldSx} />
              </LF>
              <LF label="Forma de pago">
                <FormControl fullWidth sx={fieldSx} disabled={!puedeEditarCoseguro}>
                  <Select displayEmpty value={form.coseguro_medio_pago}
                    onChange={e => setForm(f => ({ ...f, coseguro_medio_pago: e.target.value }))}
                    sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
                    <MenuItem value="Efectivo">Efectivo</MenuItem>
                    <MenuItem value="Débito / Crédito">Débito / Crédito</MenuItem>
                    <MenuItem value="Transferencia">Transferencia</MenuItem>
                  </Select>
                </FormControl>
              </LF>
            </Box>
            <Box sx={{ height: '1px', bgcolor: '#1e2535' }} />
            <Box sx={{ p: 2, display: 'flex', gap: 1.5 }}>
              {[
                { key: 'cobrado', icon: '💵', label: 'Cobrar', color: '#81c784', border: 'rgba(76,175,80,0.35)',  hoverBorder: '#66bb6a', hoverBg: 'rgba(76,175,80,0.1)',    disabled: !puedeEditarCoseguro || !form.coseguro_monto || !conceptoValido },
                { key: 'exento',  icon: '🚫', label: 'Exento', color: '#90a4ae', border: 'rgba(176,190,197,0.2)', hoverBorder: '#78909c', hoverBg: 'rgba(176,190,197,0.07)', disabled: !puedeEditarCoseguro || !conceptoValido },
              ].map(btn => (
                <Button key={btn.key} fullWidth variant="outlined"
                  disabled={btn.disabled}
                  onClick={() => ejecutarCoseguro(btn.key, conceptoFinal)}
                  sx={{ fontSize: 11, fontWeight: 700, color: btn.color, borderColor: btn.border,
                    flexDirection: 'column', gap: 0.3, py: 1,
                    '&:hover': { borderColor: btn.hoverBorder, bgcolor: btn.hoverBg },
                    '&.Mui-disabled': { color: '#37474f', borderColor: '#2a3652' } }}>
                  <span style={{ fontSize: 15 }}>{btn.icon}</span>{btn.label}
                </Button>
              ))}
            </Box>
          </>
        ) : (
          <Box sx={{ p: 2, bgcolor: col.reciboBg }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: col.labelColor, mb: 1.5 }}>
              {cs === 'cobrado' ? '✓ Coseguro cobrado' : '🚫 Marcado como exento'}
            </Typography>
            {cs === 'cobrado' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography sx={{ fontSize: 12, color: '#546e7a' }}>Monto</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#81c784' }}>
                    ${parseFloat(form.coseguro_monto || 0).toLocaleString('es-AR')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                  <Typography sx={{ fontSize: 12, color: '#546e7a' }}>Forma de pago</Typography>
                  <Typography sx={{ fontSize: 12, color: '#e0e0e0', fontWeight: 500 }}>{form.coseguro_medio_pago}</Typography>
                </Box>
              </>
            )}
            <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.06)', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: 12, color: '#546e7a' }}>Registrado por</Typography>
              <Typography sx={{ fontSize: 12, color: '#e0e0e0', fontWeight: 500 }}>{form.coseguro_registrado_por}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 12, color: '#546e7a' }}>Hora</Typography>
              <Typography sx={{ fontSize: 12, color: '#e0e0e0', fontWeight: 500 }}>{fmtFechaHora(form.coseguro_fecha)}</Typography>
            </Box>

            {puedeEditarCoseguro && !corrigiendo && !anulando && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography onClick={() => setCorrigiendo(true)}
                  sx={{ fontSize: 11, color: '#3a4f6e', cursor: 'pointer', textDecoration: 'underline',
                    '&:hover': { color: '#607d8b' } }}>
                  ¿Error? Corregir
                </Typography>
                <Typography onClick={() => setAnulando(true)}
                  sx={{ fontSize: 11, color: '#b71c1c', cursor: 'pointer', textDecoration: 'underline',
                    '&:hover': { color: '#ef5350' } }}>
                  Anular
                </Typography>
              </Box>
            )}

            {corrigiendo && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <LF label="Motivo de corrección">
                  <TextField fullWidth autoFocus
                    value={motivoCorrecion}
                    onChange={e => setMotivoCorrecion(e.target.value)}
                    placeholder="Ej: importe incorrecto, forma de pago errónea..."
                    sx={multilineFieldSx} />
                </LF>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => { setCorrigiendo(false); setMotivoCorrecion(''); }}
                    sx={{ fontSize: 11, color: '#546e7a' }}>
                    Cancelar
                  </Button>
                  <Button size="small" variant="outlined" onClick={handleCorregir}
                    disabled={!motivoCorrecion.trim()}
                    sx={{ fontSize: 11, color: '#ef5350', borderColor: 'rgba(239,83,80,0.35)',
                      '&:hover': { borderColor: '#ef5350', bgcolor: 'rgba(239,83,80,0.08)' } }}>
                    Confirmar corrección
                  </Button>
                </Box>
              </Box>
            )}

            {anulando && (
              <Box sx={{ mt: 1.5, p: '12px', bgcolor: 'rgba(183,28,28,0.06)', border: '1px solid rgba(183,28,28,0.25)', borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#ef5350' }}>
                  ⚠ Anular coseguro — esta acción borra el registro vigente
                </Typography>
                <LF label="Motivo de anulación (obligatorio)">
                  <TextField fullWidth autoFocus
                    value={motivoAnulacion}
                    onChange={e => setMotivoAnulacion(e.target.value)}
                    placeholder="Ej: error de obra social, paciente exento por resolución..."
                    sx={multilineFieldSx} />
                </LF>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => { setAnulando(false); setMotivoAnulacion(''); }}
                    sx={{ fontSize: 11, color: '#546e7a' }}>
                    Cancelar
                  </Button>
                  <Button size="small" variant="contained" onClick={handleAnular}
                    disabled={!motivoAnulacion.trim()}
                    sx={{ fontSize: 11, bgcolor: '#b71c1c', '&:hover': { bgcolor: '#c62828' },
                      '&.Mui-disabled': { bgcolor: 'rgba(183,28,28,0.2)', color: '#555' } }}>
                    Confirmar anulación
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Historial de movimientos */}
      {historial.length > 0 && (
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#455a64', textTransform: 'uppercase', letterSpacing: 1, mb: '8px' }}>
            Historial
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {historial.map((mov, i) => {
              const t = TIPO_LABEL[mov.tipo] || { label: mov.tipo, color: '#90a4ae' };
              return (
                <Box key={mov.id} sx={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  px: '10px', py: '7px', borderRadius: '6px',
                  bgcolor: i === 0 ? 'rgba(144,202,249,0.04)' : 'transparent',
                  border: '1px solid rgba(144,202,249,0.08)',
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.color }}>{t.label}</Typography>
                      {mov.monto && (
                        <Typography sx={{ fontSize: 11, color: '#81c784', fontWeight: 600 }}>
                          ${parseFloat(mov.monto).toLocaleString('es-AR')}
                        </Typography>
                      )}
                      {mov.medio_pago && (
                        <Typography sx={{ fontSize: 10, color: '#546e7a' }}>· {mov.medio_pago}</Typography>
                      )}
                    </Box>
                    {mov.motivo && (
                      <Typography sx={{ fontSize: 11, color: '#78909c', fontStyle: 'italic' }}>
                        "{mov.motivo}"
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 10, color: '#455a64' }}>
                      {mov.usuario_nombre}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 10, color: '#37474f', whiteSpace: 'nowrap', ml: 1 }}>
                    {fmtFechaHora(mov.created_at)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
